import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";

/**
 * Bottom navigation, faithful to the Tella design's read-along controls.
 *
 * Two clean states:
 *
 *   • While narrating —  [repeat] · ( play / pause ) · [volume]
 *       a centered trio: repeat-scene on the left, the big play/pause in the
 *       middle, mute/unmute on the right.
 *
 *   • At the end of a scene — re-listen pinned to the far left, and the center
 *       becomes EITHER a forward arrow inside a countdown ring that auto-advances
 *       to the next page (tap to go now), OR — on the LAST scene — a distinct,
 *       gently-pulsing finish button (never auto-jumping into comprehension).
 *       The flanking buttons drop away so the turn affordance stands alone.
 *
 * Big tap targets; works with one thumb.
 */
export function Controls({
  isPlaying,
  onPlayPause,
  onNext,
  onReplay,
  isMuted,
  onToggleMute,
  ended = false,
  isLastScene = false,
  autoAdvanceMs = 3400,
}: {
  isPlaying: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onReplay: () => void;
  isMuted: boolean;
  onToggleMute: () => void;
  /** True once narration for this scene has finished. */
  ended?: boolean;
  /** True when there is no next scene (don't auto-advance into comprehension). */
  isLastScene?: boolean;
  autoAdvanceMs?: number;
}) {
  if (ended) {
    return (
      <div className="controls controls--done">
        <button
          type="button"
          className="btn btn--relisten"
          onClick={onReplay}
          aria-label="Read this scene again"
        >
          <RepeatIcon />
        </button>

        {isLastScene ? (
          <button
            type="button"
            className="btn btn--finish"
            onClick={onNext}
            aria-label="Finish story"
          >
            <CheckIcon />
          </button>
        ) : (
          <CountdownRing durationMs={autoAdvanceMs} onComplete={onNext} onSkip={onNext} />
        )}
      </div>
    );
  }

  return (
    <div className="controls controls--playing">
      <button
        type="button"
        className="btn btn--icon"
        onClick={onReplay}
        aria-label="Read this scene again"
      >
        <RepeatIcon />
      </button>

      <button
        type="button"
        className="btn btn--round"
        onClick={onPlayPause}
        aria-label={isPlaying ? "Pause narration" : "Play narration"}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </button>

      <button
        type="button"
        className="btn btn--icon"
        onClick={onToggleMute}
        aria-label={isMuted ? "Unmute narration" : "Mute narration"}
        aria-pressed={isMuted}
      >
        {isMuted ? <VolumeMutedIcon /> : <VolumeIcon />}
      </button>
    </div>
  );
}

/**
 * Wordless auto-turn: a forward arrow with a ring that sweeps over `durationMs`,
 * then advances. Tapping advances immediately.
 */
function CountdownRing({
  durationMs,
  onComplete,
  onSkip,
}: {
  durationMs: number;
  onComplete: () => void;
  onSkip: () => void;
}) {
  // Hold the latest callback in a ref so the timer is armed exactly once on
  // mount (a re-render with a new onComplete closure won't restart the count).
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Guard against a 0 / negative / NaN duration (immediate or invalid CSS).
  const safeMs = Math.max(1, Math.floor(Number(durationMs) || 0));

  useEffect(() => {
    const id = setTimeout(() => onCompleteRef.current(), safeMs);
    return () => clearTimeout(id);
  }, [safeMs]);

  const R = 33;
  const C = 2 * Math.PI * R;
  return (
    <button
      type="button"
      className="btn btn--ring"
      onClick={onSkip}
      aria-label="Next page"
    >
      <svg viewBox="0 0 80 80" width="80" height="80" className="ring-svg" aria-hidden="true">
        <circle cx="40" cy="40" r={R} className="ring-track" />
        <circle
          cx="40"
          cy="40"
          r={R}
          className="ring-progress"
          strokeDasharray={C}
          style={{ "--ring-c": C, animationDuration: `${safeMs}ms` } as CSSProperties}
        />
      </svg>
      <span className="ring-arrow">
        <ArrowIcon />
      </span>
    </button>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
      <path d="M6 5h4v14H6zM14 5h4v14h-4z" fill="currentColor" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="36" height="36" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RepeatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path
        d="M4 12a8 8 0 108-8M4 12V6m0 6h6"
        stroke="currentColor"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VolumeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M17 9c1 1 1 5 0 6M19.5 7c2 2 2 8 0 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function VolumeMutedIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M17 9l5 6M22 9l-5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
